/**
 * The Google Sheet mirror. One way: device -> sheet, always.
 *
 * Rules this module exists to enforce, from the brief:
 *  - It never blocks, never rolls back a local write, never shows a modal.
 *  - It runs strictly AFTER the local write has committed.
 *  - Nothing it receives can affect local state. It reads the queue and writes
 *    `synced` flags. It has no code path that applies remote data, because there is
 *    no remote data: the endpoint returns an acknowledgement, not rows.
 *  - A push counts as successful ONLY on a real 2xx whose parsed body says ok:true.
 *    A 2xx carrying a Google sign-in page is a failure. This is the section 9 bug in
 *    a new costume and is the one thing this file must not get wrong.
 *
 * Mirror bookkeeping lives in the `meta` table, never in `kv`. Putting it in `kv`
 * would make each push dirty a row, which would queue another push, forever.
 */
import type { PortableRow } from './engine.ts';

export const MIRROR_ENDPOINT = '/api/mirror';
export const BATCH_LIMIT = 500;

export interface PushPayload {
  deviceId: string;
  kv: PortableRow[];
  views?: Record<string, unknown[][]>;
}

export interface TransportResult {
  ok: boolean;                 // strict: 2xx AND parsed body.ok === true
  status: number | null;
  error: string | null;        // verbatim, shown to the user unedited
  body: unknown;
}

/** What the Data screen displays. Observed outcomes only. */
export interface MirrorState {
  lastSuccessAt: number | null;
  lastAttemptAt: number | null;
  lastError: string | null;
  lastRowsPushed: number | null;
  consecutiveFailures: number;
}

export const EMPTY_STATE: MirrorState = {
  lastSuccessAt: null, lastAttemptAt: null, lastError: null,
  lastRowsPushed: null, consecutiveFailures: 0,
};

/** The slice of the store the mirror is allowed to touch. Deliberately narrow:
 *  it can read the queue and set sync flags, and it cannot write app state. */
/** Builds the four derived tabs. Injected so this module stays free of plan imports:
 *  the data layer must not depend on plan content. Returning null skips them. */
export type ViewsBuilder = () => Promise<Record<string, unknown[][]> | null>;

export interface MirrorStore {
  readonly deviceId: string;
  unsynced(limit?: number): Promise<PortableRow[]>;
  unsyncedCount(): Promise<number>;
  markSynced(rows: Pick<PortableRow, 'k' | 'updated_at'>[]): Promise<number>;
  getMeta(k: string): Promise<string | undefined>;
  setMeta(k: string, v: string): Promise<void>;
}

const META_KEY = 'mirror_state';

/** The real transport. Same-origin, so no CORS and no secret in this bundle;
 *  the Pages Function holds the credential and forwards to Apps Script. */
export async function httpTransport(payload: PushPayload): Promise<TransportResult> {
  let res: Response;
  try {
    res = await fetch(MIRROR_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    return { ok: false, status: null, body: null,
             error: `network: ${e instanceof Error ? e.message : String(e)}` };
  }

  const text = await res.text().catch(() => '');
  if (!res.ok) {
    return { ok: false, status: res.status, body: text.slice(0, 300),
             error: `HTTP ${res.status}: ${text.slice(0, 200) || res.statusText}` };
  }

  // A 2xx is not success. Parse it and require our own acknowledgement.
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return { ok: false, status: res.status, body: text.slice(0, 300),
             error: `HTTP ${res.status} but the body was not JSON. First 200 chars: ${text.slice(0, 200)}` };
  }
  const ack = body as { ok?: unknown; error?: unknown };
  if (ack?.ok !== true) {
    return { ok: false, status: res.status, body,
             error: `endpoint refused: ${typeof ack?.error === 'string' ? ack.error : JSON.stringify(body).slice(0, 200)}` };
  }
  return { ok: true, status: res.status, body, error: null };
}

export class Mirror {
  private readonly store: MirrorStore;
  private readonly transport: (p: PushPayload) => Promise<TransportResult>;
  private readonly clock: () => number;
  private inFlight: Promise<MirrorState> | null = null;
  /** Set by the app once plan logic is available. */
  buildViews: ViewsBuilder | null = null;

  constructor(
    store: MirrorStore,
    transport: (p: PushPayload) => Promise<TransportResult> = httpTransport,
    clock: () => number = Date.now,
  ) {
    this.store = store;
    this.transport = transport;
    this.clock = clock;
  }

  async state(): Promise<MirrorState> {
    const raw = await this.store.getMeta(META_KEY);
    if (!raw) return { ...EMPTY_STATE };
    try { return { ...EMPTY_STATE, ...(JSON.parse(raw) as MirrorState) }; }
    catch { return { ...EMPTY_STATE }; }
  }

  private async save(s: MirrorState): Promise<void> {
    await this.store.setMeta(META_KEY, JSON.stringify(s));
  }

  /**
   * Push whatever is queued. Never throws. Never touches app state on failure.
   * Concurrent calls share one in-flight push rather than double-sending.
   */
  push(opts: { includeViews?: boolean } = {}): Promise<MirrorState> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.runPush(opts.includeViews === true)
      .finally(() => { this.inFlight = null; });
    return this.inFlight;
  }

  private async runPush(includeViews: boolean): Promise<MirrorState> {
    const prev = await this.state();
    let batch: PortableRow[];
    try {
      batch = await this.store.unsynced(BATCH_LIMIT);
    } catch (e) {
      const s: MirrorState = { ...prev, lastAttemptAt: this.clock(),
        lastError: `could not read the queue: ${msg(e)}`,
        consecutiveFailures: prev.consecutiveFailures + 1 };
      await this.save(s).catch(() => {});
      return s;
    }

    if (batch.length === 0) {
      // Nothing to do is not a success and must not be reported as one.
      const s: MirrorState = { ...prev, lastAttemptAt: this.clock() };
      await this.save(s).catch(() => {});
      return s;
    }

    // Derived views are 112+ rows recomputed from scratch, so they ride along with
    // foreground, background and manual pushes but not the every-few-minutes one.
    // A failure to build them must never block the kv backup, which is the real one.
    let views: Record<string, unknown[][]> | undefined;
    if (includeViews && this.buildViews) {
      try { views = (await this.buildViews()) ?? undefined; } catch { views = undefined; }
    }

    let result: TransportResult;
    try {
      result = await this.transport({ deviceId: this.store.deviceId, kv: batch, views });
    } catch (e) {
      result = { ok: false, status: null, body: null, error: `transport threw: ${msg(e)}` };
    }

    const at = this.clock();
    if (!result.ok) {
      // Local state and the queue are both untouched. That is the whole contract.
      const s: MirrorState = { ...prev, lastAttemptAt: at,
        lastError: result.error ?? 'unknown failure',
        consecutiveFailures: prev.consecutiveFailures + 1 };
      await this.save(s).catch(() => {});
      return s;
    }

    // Only now, after an observed 2xx + ok:true, do we mark anything mirrored.
    // markSynced matches on (k, updated_at), so a row rewritten mid-push stays queued.
    try {
      await this.store.markSynced(batch);
    } catch (e) {
      const s: MirrorState = { ...prev, lastAttemptAt: at,
        lastError: `push succeeded but the queue could not be updated: ${msg(e)}`,
        consecutiveFailures: prev.consecutiveFailures + 1 };
      await this.save(s).catch(() => {});
      return s;
    }

    const s: MirrorState = { lastSuccessAt: at, lastAttemptAt: at, lastError: null,
      lastRowsPushed: batch.length, consecutiveFailures: 0 };
    await this.save(s).catch(() => {});
    return s;
  }

  /** Drains a backlog larger than one batch, e.g. after a week offline. */
  async drain(maxBatches = 20, includeViews = true): Promise<MirrorState> {
    let s = await this.state();
    for (let i = 0; i < maxBatches; i++) {
      const before = await this.store.unsyncedCount().catch(() => 0);
      if (before === 0) break;
      // Views only on the final batch of a drain; earlier batches are pure backlog.
      s = await this.push({ includeViews: includeViews && before <= BATCH_LIMIT });
      if (s.lastError) break;                       // stop on first failure; retry later
      const after = await this.store.unsyncedCount().catch(() => 0);
      if (after >= before) break;                   // no progress: do not spin
    }
    return s;
  }
}

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** Exponential backoff so a broken endpoint is not hammered. Capped at 30 minutes. */
export function backoffMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return 0;
  return Math.min(30 * 60_000, 15_000 * 2 ** (consecutiveFailures - 1));
}
